import { useEffect, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router';

import { apiConnector } from '@/api/api.connector';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES } from '@/shared/routes';
import { getUserToken, removeUserToken } from '@/shared/services/user-hash';
import { MainLoader } from '@/shared/ui';
import { isInWebView } from '@/shared/webview/env';

import AuthorizedProvider from './authorized.provider';

const ProtectedRoute: React.FC = () => {
  const { navigate } = useAppNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      // В WebView нет web-токена в cookie: авторизация — обмен суперапп-сессии
      // нативы на JWT (ssoAuthorize, in-memory в apiConnector). Гейтим по нему.
      if (isInWebView()) {
        const ok =
          apiConnector.isSsoAuthorized() || (await apiConnector.ssoAuthorize());
        // При провале ssoAuthorize сам закрывает мини-апп (closeWebView) —
        // остаёмся в загрузке, веб-версией не подменяемся.
        if (ok) setIsLoading(false);
        return;
      }

      const token = getUserToken();
      const params = new URLSearchParams(window.location.search);
      const handoffToken = params.get('handoff_token');

      if (handoffToken && !token?.accessToken) {
        apiConnector
          .handoffToken(handoffToken)
          .then(() => {
            setIsLoading(false);
          })
          .catch(() => {
            removeUserToken();
            navigate(`/${ROUTES.login}`, { replace: true });
          });
        params.delete('handoff_token');
        setSearchParams(params, { replace: true });
        return;
      } else if (handoffToken) {
        params.delete('handoff_token');
        setSearchParams(params, { replace: true });
      }

      if (!token?.accessToken) {
        removeUserToken();
        navigate(`/${ROUTES.login}`, { replace: true });
      } else {
        setIsLoading(false);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [location.pathname]);

  if (isLoading) {
    return <MainLoader fullScreen />;
  }

  return (
    <AuthorizedProvider>
      <Outlet />
    </AuthorizedProvider>
  );
};

export default ProtectedRoute;
